<script lang="ts">
import type { StudyScoreBoardSlideProps } from '$lib/components/study/slide-layout-types'
import StudySlide from '../StudySlide.svelte'
import StudySlideContent from '../StudySlideContent.svelte'

/**
 * Geographic scoreboard slide (`s4`): `.tc` with spotlight rank rows, insight `.hb`,
 * and a `.cc` card with a full ranked bar list + concentration callout.
 * `eyebrow`, `headline`, `intro`, `insightHtml`, and `calloutBodyHtml` use `{@html}` where marked.
 */
let props: StudyScoreBoardSlideProps = $props()

const headlineStyle = props.headlineStyle ?? 'margin-bottom:.82rem;'
const introStyle = props.introStyle ?? 'margin-bottom:.95rem;'
const tcStyle = props.tcStyle ?? 'gap:1.9rem;align-items:start;'
const tcWeighted = props.tcWeighted ?? true

const spotlightMax = $derived(props.spotlightRows[0]?.claims ?? 0)
const leaderboardMax = $derived(props.leaderboardRows[0]?.claims ?? 0)
</script>

<StudySlide
  id={props.id}
  active={props.active}
  extraBottom={props.extraBottom}
  background={props.background}
  slideClass={props.slideClass}
>
  <StudySlideContent
    maxWidth={props.maxWidth ?? '980px'}
    contentClass={props.contentClass}
    style={props.contentStyle}
  >
    <div class="ey">{@html props.eyebrow}</div>
    <div class="tc" class:w={tcWeighted} style={tcStyle}>
      <div>
        <div class="hl" style={headlineStyle}>{@html props.headline}</div>
        <p class="bd" style={introStyle}>{@html props.intro}</p>

        <div style="display:flex;flex-direction:column;gap:.42rem;margin-bottom:.9rem;">
          {#each props.spotlightRows as row, i}
            {@const rankColors = ['var(--g)', 'rgba(17,160,165,.65)', 'rgba(17,160,165,.4)']}
            {@const rankColor = rankColors[Math.min(i, rankColors.length - 1)]}
            {@const barPct = (spotlightMax > 0 ? (row.claims / spotlightMax) * 100 : 0).toFixed(1)}
            <div
              style="display:flex;align-items:center;gap:.85rem;padding:.72rem 1rem;background:{i ===
              0
                ? 'var(--rank-row-bg-strong)'
                : 'var(--rank-row-bg-soft)'};border:1px solid {i === 0
                ? 'var(--rank-border-strong)'
                : 'var(--rank-border-soft)'};border-radius:8px;"
            >
              <div
                style="font-size:.58rem;color:var(--mu);font-family:'DM Mono',monospace;min-width:1rem;opacity:.6;"
              >
                #{i + 1}
              </div>
              <div
                style="font-family:'DM Serif Display',serif;font-size:1.15rem;color:{rankColor};min-width:2.4rem;"
              >
                {row.state}
              </div>
              <div
                style="flex:1;height:5px;background:var(--bar-track);border-radius:3px;overflow:hidden;"
              >
                <div
                  style="height:100%;width:{barPct}%;background:{rankColor};border-radius:3px;"
                ></div>
              </div>
              <div
                style="font-size:.72rem;font-family:'DM Mono',monospace;color:{rankColor};min-width:3rem;text-align:right;"
              >
                {row.valueLabel}
              </div>
            </div>
          {/each}
        </div>

        <div class="hb">
          <p>{@html props.insightHtml}</p>
        </div>
      </div>

      <div>
        <div class="cc">
          <div class="ct">{@html props.chartTitle}</div>
          <div class="cs">{@html props.chartSubtitle}</div>
          <div style="margin-top:.95rem;display:flex;flex-direction:column;gap:.32rem;">
            {#each props.leaderboardRows as row, i}
              {@const barPct = (
                leaderboardMax > 0 ? (row.claims / leaderboardMax) * 100 : 0
              ).toFixed(1)}
              {@const alpha = (1 - i * 0.07).toFixed(2)}
              <div style="display:flex;align-items:center;gap:.72rem;">
                <div
                  style="font-size:.58rem;color:var(--mu);font-family:'DM Mono',monospace;min-width:1.2rem;text-align:right;opacity:.5;"
                >
                  {i + 1}
                </div>
                <div
                  style="font-size:.75rem;font-weight:700;color:{i < 5
                    ? 'var(--g)'
                    : 'var(--t2)'};min-width:2.2rem;letter-spacing:.03em;"
                >
                  {row.state}
                </div>
                <div
                  style="flex:1;height:8px;background:var(--bar-track-lg);border-radius:4px;overflow:hidden;"
                >
                  <div
                    style="height:100%;width:{barPct}%;background:rgba(17,160,165,{alpha});border-radius:4px;"
                  ></div>
                </div>
                <div
                  style="font-size:.68rem;font-family:'DM Mono',monospace;color:{i < 5
                    ? 'var(--g)'
                    : 'var(--mu)'};min-width:3.2rem;text-align:right;"
                >
                  {row.valueLabel}
                </div>
              </div>
            {/each}
          </div>

          <div
            style="margin-top:1rem;padding:.72rem .9rem;background:rgba(17,160,165,.07);border:1px solid rgba(17,160,165,.2);border-radius:7px;display:flex;align-items:center;gap:.85rem;"
          >
            <div
              style="font-family:'DM Serif Display',serif;font-size:1.6rem;color:var(--g);line-height:1;"
            >
              {props.calloutPct}
            </div>
            <div style="font-size:.65rem;color:var(--t2);line-height:1.5;">
              {@html props.calloutBodyHtml}
            </div>
          </div>
        </div>
      </div>
    </div>
  </StudySlideContent>
</StudySlide>
